import { ThreadMessage } from '@assistant-ui/core';

export type MessagePart = ThreadMessage['content'][number];

export type ToolCallPart = Extract<MessagePart, { type: 'tool-call' }>;

export const isVisiblePart = (part: MessagePart) =>
  ((part.type === 'text' || part.type === 'reasoning') &&
    part.text.length > 0) ||
  part.type === 'tool-call';

/** An approval ends the run, so a call waiting on the user is not running. */
export const isToolCallRunning = (message: ThreadMessage, part: MessagePart) =>
  part.type === 'tool-call' &&
  part.result === undefined &&
  message.role === 'assistant' &&
  message.status.type === 'running';

export const isAwaitingApproval = (message: ThreadMessage, part: MessagePart) =>
  part.type === 'tool-call' &&
  part.result === undefined &&
  message.role === 'assistant' &&
  message.status.type === 'requires-action';

export const hasToolCallFailed = (part: ToolCallPart) => {
  if (part.isError === true) {
    return true;
  }
  const result = part.result as
    | { error?: unknown; status?: unknown }
    | null
    | undefined;
  return (
    typeof result === 'object' &&
    result !== null &&
    (result.status === 'error' ||
      (typeof result.error === 'string' && result.error.length > 0))
  );
};

export const toolCallLabel = (part: ToolCallPart) => {
  const purpose = (part.args as { purpose?: unknown } | null)?.purpose;
  return typeof purpose === 'string' && purpose.trim().length > 0
    ? purpose.trim()
    : part.toolName;
};

export const isReasoningRunning = (message: ThreadMessage, part: MessagePart) =>
  part.type === 'reasoning' &&
  message.role === 'assistant' &&
  message.status.type === 'running' &&
  message.content[message.content.length - 1] === part;

/**
 * Show the typing indicator only while nothing on screen is moving: before the first token, and between a tool
 * result and the reply that uses it.
 */
export const isAwaitingOutput = (
  isRunning: boolean,
  messages: readonly ThreadMessage[],
) => {
  if (!isRunning) {
    return false;
  }
  const last = messages[messages.length - 1];
  if (last?.role !== 'assistant') {
    return true;
  }
  const visible = last.content.filter(isVisiblePart);
  const lastPart = visible[visible.length - 1];
  if (!lastPart) {
    return true;
  }
  if (lastPart.type === 'reasoning') {
    return !isReasoningRunning(last, lastPart);
  }
  return lastPart.type === 'tool-call' && !isToolCallRunning(last, lastPart);
};
